// Service Convention d'honoraires — une convention par dossier.
// Pattern projet : écriture via RPC SECURITY DEFINER (upsert_fee_agreement),
// lecture directe via PostgREST (RLS = can_view_dossier_billing).
import { supabase } from '../../lib/supabaseClient';
import type { Tables, Database, Json } from '../../types/database.types';

export type FeeAgreement = Tables<'dossier_fee_agreements'>;
export type FeeAgreementMode = Database['public']['Enums']['fee_agreement_mode'];

/** Prestation couverte par la convention (élément de la liste `services`). */
export interface FeeService {
  label: string;
}

export const FEE_AGREEMENT_MODE_LABELS: Record<FeeAgreementMode, string> = {
  forfait: 'Forfait',
  hourly: 'Taux horaire',
  mixed: 'Mixte',
};

/** Parse le jsonb `services` en liste typée, robuste aux données malformées. */
export function parseFeeServices(raw: FeeAgreement['services']): FeeService[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((s) => (s && typeof s === 'object' && 'label' in s ? { label: String((s as { label: unknown }).label) } : null))
    .filter((s): s is FeeService => s !== null && s.label.trim().length > 0);
}

export interface UpsertFeeAgreementInput {
  dossier_id: string;
  billing_mode: FeeAgreementMode;
  forfait_amount?: number | null;
  hourly_rate?: number | null;
  services: FeeService[];
  success_fee_rate?: number | null;
  success_fee_basis?: string | null;
  success_fee_notes?: string | null;
  signed_at?: string | null;
  notes?: string | null;
}

// ─── SELECT ──────────────────────────────────────────────────────────────

export async function getFeeAgreement(dossierId: string): Promise<FeeAgreement | null> {
  const { data, error } = await supabase
    .from('dossier_fee_agreements')
    .select('*')
    .eq('dossier_id', dossierId)
    .maybeSingle();
  if (error) throw new Error(`Failed to fetch fee agreement: ${error.message}`);
  return data;
}

// ─── UPSERT (RPC) ─────────────────────────────────────────────────────────

export async function upsertFeeAgreement(input: UpsertFeeAgreementInput): Promise<FeeAgreement> {
  const { data, error } = await supabase.rpc('upsert_fee_agreement', {
    p_dossier_id: input.dossier_id,
    p_billing_mode: input.billing_mode,
    p_forfait_amount: input.forfait_amount ?? undefined,
    p_hourly_rate: input.hourly_rate ?? undefined,
    p_services: input.services as unknown as Json,
    p_success_fee_rate: input.success_fee_rate ?? undefined,
    p_success_fee_basis: input.success_fee_basis ?? undefined,
    p_success_fee_notes: input.success_fee_notes ?? undefined,
    p_signed_at: input.signed_at ?? undefined,
    p_notes: input.notes ?? undefined,
  });
  if (error) throw new Error(`Failed to save fee agreement: ${error.message}`);
  return data;
}

/** Provision demandée au client, en euros TTC (null = aucune). La convention doit exister. */
export async function setFeeAgreementProvision(dossierId: string, amount: number | null): Promise<void> {
  const { error } = await supabase.rpc('set_fee_agreement_provision', {
    p_dossier_id: dossierId,
    // La RPC attend un nombre ; null est accepté par la base (provision retirée).
    p_amount: amount as number,
  });
  if (error) throw new Error(`Failed to save provision: ${error.message}`);
}
